import { useState, useEffect, useMemo } from "react";
import { HiCog6Tooth, HiPencil, HiPlus, HiEye, HiEyeSlash, HiXMark, HiTrash, HiCheck, HiBars3 } from "react-icons/hi2";
import { useAppAccess } from "../hooks/useAppAccess";
import { useApps, useCreateApp, useUpdateApp, useDeleteApp } from "../hooks/useApps";
import { useUserPreferences, useSetUserPreferences } from "../hooks/useUserPreferences";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface App {
  id: string;
  name: string;
  description?: string;
  url: string;
  iconUrl?: string;
  category: string;
  sortOrder: number;
  isActive: boolean;
}

interface AppsGridProps {
  userId: string;
}

// Gradient mapping for app cards by category
const gradientMap: Record<string, string> = {
  Financials: "var(--gradient-financials)",
  Projects: "var(--gradient-projects)",
  Time: "var(--gradient-time)",
  Admin: "var(--gradient-admin)",
  Misc: "var(--gradient-misc)",
  Teamwork: "var(--gradient-teamwork)",
  Internal: "var(--gradient-internal)",
  External: "var(--gradient-external)",
  HR: "var(--gradient-hr)",
  Events: "var(--gradient-events)",
  Dev: "var(--gradient-dev)",
};

export default function AppsGrid({ userId }: AppsGridProps) {
  // PostgreSQL hooks
  const { data: appsData, isLoading: appsLoading } = useApps();
  const { data: userPrefs } = useUserPreferences(userId);
  const setPreferencesMutation = useSetUserPreferences();
  const { canAccess, isAdmin } = useAppAccess();
  const { user } = useAuthContext();
  const createAppMutation = useCreateApp();
  const updateAppMutation = useUpdateApp();
  const deleteAppMutation = useDeleteApp();
  const [confirmDeleteApp, setConfirmDeleteApp] = useState(false);

  const [orderedApps, setOrderedApps] = useState<App[]>([]);
  const [orderedCategories, setOrderedCategories] = useState<string[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [hiddenAppIds, setHiddenAppIds] = useState<Set<string>>(new Set());
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [creatingInCategory, setCreatingInCategory] = useState<string | null>(null);

  // Reset confirm delete after 3 seconds
  useEffect(() => {
    if (confirmDeleteApp) {
      const timer = setTimeout(() => setConfirmDeleteApp(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [confirmDeleteApp]);

  // Reset confirm state when dialog closes
  useEffect(() => {
    if (!editingApp) setConfirmDeleteApp(false);
  }, [editingApp]);

  // Cast userPrefs to expected shape
  const prefs = userPrefs as { appOrder?: string[]; hiddenApps?: string[]; categoryOrder?: string[] } | undefined;

  // Initialize ordered apps, hidden apps, and category order when data loads
  useEffect(() => {
    if (!appsData?.data) return;

    const sourceApps = appsData.data.filter((app) => app.isActive);

    const savedOrder = prefs?.appOrder;
    const savedHidden = prefs?.hiddenApps;
    const savedCategoryOrder = prefs?.categoryOrder;

    // Load hidden apps
    if (savedHidden && savedHidden.length > 0) {
      setHiddenAppIds(new Set(savedHidden));
    }

    // Load category order
    if (savedCategoryOrder && savedCategoryOrder.length > 0) {
      setOrderedCategories(savedCategoryOrder);
    }

    if (savedOrder && savedOrder.length > 0) {
      // Sort by user's saved order
      const orderMap = new Map(savedOrder.map((id, index) => [id, index]));
      const sorted = [...sourceApps].sort((a, b) => {
        const aOrder = orderMap.get(a.id) ?? Infinity;
        const bOrder = orderMap.get(b.id) ?? Infinity;
        if (aOrder === bOrder) return a.sortOrder - b.sortOrder;
        return aOrder - bOrder;
      });
      setOrderedApps(sorted);
    } else {
      // Use default sort order
      const sorted = [...sourceApps].sort((a, b) => a.sortOrder - b.sortOrder);
      setOrderedApps(sorted);
    }
  }, [appsData, prefs]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = orderedApps.findIndex((app) => app.id === active.id);
      const newIndex = orderedApps.findIndex((app) => app.id === over.id);

      const newOrder = arrayMove(orderedApps, oldIndex, newIndex);
      setOrderedApps(newOrder);

      // Save the new order to user preferences
      await setPreferencesMutation.mutateAsync({
        userId,
        preferences: {
          ...prefs,
          appOrder: newOrder.map((app) => app.id),
        },
      });
    }
  };

  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = groupedApps.findIndex(([cat]) => cat === active.id);
    const newIndex = groupedApps.findIndex(([cat]) => cat === over.id);

    const newOrder = arrayMove(groupedApps, oldIndex, newIndex).map(([cat]) => cat);
    setOrderedCategories(newOrder);

    // Save to preferences
    await setPreferencesMutation.mutateAsync({
      userId,
      preferences: {
        ...prefs,
        categoryOrder: newOrder,
      },
    });
  };

  const handleResetOrder = async () => {
    if (!appsData?.data) return;
    const sourceApps = appsData.data.filter((app) => app.isActive);
    const sorted = [...sourceApps].sort((a, b) => a.sortOrder - b.sortOrder);
    setOrderedApps(sorted);
    setOrderedCategories([]);

    // Clear saved order from preferences
    const { appOrder: _, categoryOrder: __, ...restPrefs } = prefs || {};
    await setPreferencesMutation.mutateAsync({
      userId,
      preferences: restPrefs,
    });
  };

  const toggleAppVisibility = async (appId: string) => {
    const newHidden = new Set(hiddenAppIds);
    if (newHidden.has(appId)) {
      newHidden.delete(appId);
    } else {
      newHidden.add(appId);
    }
    setHiddenAppIds(newHidden);

    // Save to preferences
    await setPreferencesMutation.mutateAsync({
      userId,
      preferences: {
        ...prefs,
        hiddenApps: Array.from(newHidden),
      },
    });
  };

  // Filter apps by access permissions
  const accessibleApps = useMemo(
    () => orderedApps.filter((app) => canAccess(app)),
    [orderedApps, canAccess]
  );

  // Filter out hidden apps for display
  const visibleApps = accessibleApps.filter((app) => !hiddenAppIds.has(app.id));

  // Group visible apps by category and sort by custom order
  const groupedApps = useMemo(() => {
    const groups: Record<string, App[]> = {};
    visibleApps.forEach((app) => {
      if (!groups[app.category]) {
        groups[app.category] = [];
      }
      groups[app.category].push(app);
    });
    // Convert to array and sort by custom category order
    const entries = Object.entries(groups);
    if (orderedCategories.length > 0) {
      const orderMap = new Map(orderedCategories.map((cat, index) => [cat, index]));
      entries.sort((a, b) => {
        const aOrder = orderMap.get(a[0]) ?? Infinity;
        const bOrder = orderMap.get(b[0]) ?? Infinity;
        return aOrder - bOrder;
      });
    }
    return entries;
  }, [visibleApps, orderedCategories]);

  if (appsLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-bold text-5xl text-maven-gray-800 dark:text-white">Essential / Daily Apps</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-lg bg-gray-200 dark:bg-white/10 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-bold text-5xl text-maven-gray-800 dark:text-white">Essential / Daily Apps</h2>
        <div className="flex items-center gap-2">
          {isReordering && (
            <button
              onClick={handleResetOrder}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-white/50 dark:hover:text-white px-3 py-1.5 rounded hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Reset Order
            </button>
          )}
          <button
            onClick={() => {
              setIsReordering(!isReordering);
              if (!isReordering) setIsEditMode(false);
            }}
            className={`text-sm px-3 py-1.5 rounded transition-colors ${
              isReordering
                ? "bg-maven-teal-500 text-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10"
            }`}
          >
            {isReordering ? "Done" : "Reorder"}
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                setIsEditMode(!isEditMode);
                if (!isEditMode) setIsReordering(false);
              }}
              className={`p-2 rounded transition-colors ${
                isEditMode
                  ? "bg-maven-teal-500 text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10"
              }`}
              title={isEditMode ? "Exit edit mode" : "Edit apps"}
            >
              <HiPencil className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowVisibilityModal(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 rounded transition-colors"
            title="Show/hide apps"
          >
            <HiCog6Tooth className="w-5 h-5" />
          </button>
        </div>
      </div>

      {isReordering ? (
        <SortableCategoriesContainer
          groupedApps={groupedApps}
          onCategoryDragEnd={handleCategoryDragEnd}
          onAppDragEnd={handleDragEnd}
        />
      ) : (
        <div className="space-y-8">
          {groupedApps.map(([category, apps]) => (
            <div key={category}>
              <h3 className="text-lg font-semibold text-maven-gray-800 dark:text-white mb-4">
                {category}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {apps.map((app) => (
                  <AppCard
                    key={app.id}
                    app={app}
                    isEditMode={isEditMode}
                    onEdit={() => setEditingApp(app)}
                  />
                ))}
                {isEditMode && (
                  <button
                    onClick={() => setCreatingInCategory(category)}
                    className="h-24 rounded-lg border-2 border-dashed border-gray-300 hover:border-maven-teal-500 hover:bg-maven-teal-50 transition-colors flex items-center justify-center"
                  >
                    <HiPlus className="w-8 h-8 text-gray-400 dark:text-white/50" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Visibility Modal */}
      {showVisibilityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <HiCog6Tooth className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Show/Hide Apps</h3>
              </div>
              <button
                onClick={() => setShowVisibilityModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {/* App List by Category */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {Object.entries(
                  accessibleApps.reduce((acc, app) => {
                    if (!acc[app.category]) acc[app.category] = [];
                    acc[app.category].push(app);
                    return acc;
                  }, {} as Record<string, App[]>)
                ).map(([category, apps]) => {
                  const allHidden = apps.every((app) => hiddenAppIds.has(app.id));

                  const toggleCategoryVisibility = async () => {
                    const newHidden = new Set(hiddenAppIds);
                    if (allHidden) {
                      // Show all
                      apps.forEach((app) => newHidden.delete(app.id));
                    } else {
                      // Hide all
                      apps.forEach((app) => newHidden.add(app.id));
                    }
                    setHiddenAppIds(newHidden);
                    await setPreferencesMutation.mutateAsync({
                      userId,
                      preferences: {
                        ...prefs,
                        hiddenApps: Array.from(newHidden),
                      },
                    });
                  };

                  return (
                    <div key={category} className="bg-gray-50 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2">
                        <span
                          className="inline-block text-xs font-semibold text-white px-3 py-1 rounded-full"
                          style={{ background: gradientMap[category] || gradientMap["Misc"] }}
                        >
                          {category}
                        </span>
                        <button
                          onClick={toggleCategoryVisibility}
                          className={`p-1 rounded hover:bg-gray-100 ${allHidden ? "text-gray-400" : "text-gray-600"}`}
                          title={allHidden ? "Show all" : "Hide all"}
                        >
                          {allHidden ? (
                            <HiEyeSlash className="w-4 h-4" />
                          ) : (
                            <HiEye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div className="space-y-1">
                        {apps.map((app) => {
                          const isHidden = hiddenAppIds.has(app.id);
                          return (
                            <div
                              key={app.id}
                              className={`flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50 ${
                                isHidden ? "opacity-50" : ""
                              }`}
                            >
                              <span className="text-sm text-gray-900">{app.name}</span>
                              <button
                                onClick={() => toggleAppVisibility(app.id)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                title={isHidden ? "Show app" : "Hide app"}
                              >
                                {isHidden ? (
                                  <HiEyeSlash className="w-4 h-4" />
                                ) : (
                                  <HiEye className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowVisibilityModal(false)}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-600"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit App Dialog */}
      <Dialog open={!!editingApp} onOpenChange={(open) => !open && setEditingApp(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit App</DialogTitle>
          </DialogHeader>
          {editingApp && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                await updateAppMutation.mutateAsync({
                  id: editingApp.id,
                  name: formData.get("name") as string,
                  url: formData.get("url") as string,
                  description: formData.get("description") as string || undefined,
                  userEmail: user?.email,
                });
                setEditingApp(null);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingApp.name}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  name="url"
                  type="url"
                  defaultValue={editingApp.url}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <textarea
                  id="description"
                  name="description"
                  defaultValue={editingApp.description || ""}
                  placeholder="Shows on hover"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-maven-teal-500 resize-none"
                />
              </div>
              <div className="flex justify-between items-center pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (confirmDeleteApp) {
                      deleteAppMutation.mutate({ id: editingApp.id, userEmail: user?.email });
                      setEditingApp(null);
                    } else {
                      setConfirmDeleteApp(true);
                    }
                  }}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors inline-flex items-center gap-1.5 ${
                    confirmDeleteApp
                      ? "bg-red-100 text-red-600"
                      : "text-gray-400 hover:text-red-600"
                  }`}
                >
                  {confirmDeleteApp ? (
                    <>
                      <HiCheck className="w-4 h-4" />
                      Click to confirm
                    </>
                  ) : (
                    <>
                      <HiTrash className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingApp(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateAppMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-lg hover:bg-maven-teal-600 disabled:opacity-50"
                  >
                    {updateAppMutation.isPending ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Create App Dialog */}
      <Dialog open={!!creatingInCategory} onOpenChange={(open) => !open && setCreatingInCategory(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New App to {creatingInCategory}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              await createAppMutation.mutateAsync({
                name: formData.get("name") as string,
                url: formData.get("url") as string,
                category: creatingInCategory!,
                description: formData.get("description") as string || undefined,
                createdBy: user?.email || "unknown",
              });
              setCreatingInCategory(null);
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="new-name">Name</Label>
              <Input
                id="new-name"
                name="name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-url">URL</Label>
              <Input
                id="new-url"
                name="url"
                type="url"
                placeholder="https://"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-description">Description (optional)</Label>
              <Input
                id="new-description"
                name="description"
                placeholder="Shows on hover"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => setCreatingInCategory(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createAppMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-lg hover:bg-maven-teal-600 disabled:opacity-50"
              >
                {createAppMutation.isPending ? "Saving..." : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableAppCard({ app, index }: { app: App; index: number }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
  };

  const gradient = gradientMap[app.category] || gradientMap["Misc"];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="select-none"
      {...attributes}
      {...listeners}
    >
      <div
        style={{ background: gradient }}
        className={`h-24 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing flex flex-col p-4 ${
          isDragging ? "opacity-50" : index % 2 === 0 ? "wiggle-even" : "wiggle-odd"
        }`}
      >
        <div className="flex-1 flex items-center justify-center text-white">
          <span className="text-sm font-bold text-center">{app.name}</span>
        </div>
      </div>
    </div>
  );
}

// Container for sortable categories (outer level)
function SortableCategoriesContainer({
  groupedApps,
  onCategoryDragEnd,
  onAppDragEnd,
}: {
  groupedApps: [string, App[]][];
  onCategoryDragEnd: (event: DragEndEvent) => void;
  onAppDragEnd: (event: DragEndEvent) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onCategoryDragEnd}
    >
      <SortableContext
        items={groupedApps.map(([cat]) => cat)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-8">
          {groupedApps.map(([category, apps]) => (
            <SortableCategory
              key={category}
              category={category}
              apps={apps}
              onAppDragEnd={onAppDragEnd}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// Sortable category (for reordering categories)
function SortableCategory({
  category,
  apps,
  onAppDragEnd,
}: {
  category: string;
  apps: App[];
  onAppDragEnd: (event: DragEndEvent) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? "opacity-50" : ""}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          {...attributes}
          {...listeners}
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1 -ml-1"
          title="Drag to reorder category"
        >
          <HiBars3 className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold text-maven-gray-800 dark:text-white">
          {category}
        </h3>
      </div>
      <SortableCategoryGrid apps={apps} onReorder={onAppDragEnd} />
    </div>
  );
}

// Sortable grid within a category (for reordering apps)
function SortableCategoryGrid({
  apps,
  onReorder,
}: {
  apps: App[];
  onReorder: (event: DragEndEvent) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onReorder}
    >
      <SortableContext
        items={apps.map((app) => app.id)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {apps.map((app, index) => (
            <SortableAppCard key={app.id} app={app} index={index} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function AppCard({
  app,
  isEditMode,
  onEdit,
}: {
  app: App;
  isEditMode?: boolean;
  onEdit?: () => void;
}) {
  const gradient = gradientMap[app.category] || gradientMap["Misc"];

  return (
    <div className="relative">
      <a
        href={app.url}
        target="_blank"
        rel="noopener noreferrer"
        className="h-24 rounded-lg overflow-hidden hover:opacity-90 transition-opacity flex flex-col p-4"
        style={{ background: gradient }}
      >
        <div className="flex-1 flex items-center justify-center text-white">
          <span className="text-sm font-bold text-center">{app.name}</span>
        </div>
      </a>
      {isEditMode && onEdit && (
        <button
          onClick={onEdit}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 hover:bg-white text-gray-700 flex items-center justify-center transition-colors shadow-sm"
        >
          <HiPencil className="w-3.5 h-3.5" />
        </button>
      )}
      {!isEditMode && app.description && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 text-white text-xs font-bold flex items-center justify-center transition-colors"
              onClick={(e) => e.preventDefault()}
            >
              i
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {app.description}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
